import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import PageLayout from '@/components/PageLayout';
import { useSettings } from '@/contexts/SettingsContext';
import { toast } from '@/components/ui/use-toast';
import { useAuth } from '@/contexts/AuthProvider';
import { supabase } from '@/lib/supabase';
import { PostgrestError } from '@supabase/supabase-js';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { useCar } from '@/contexts/CarContext';
import { useI18n } from '@/i18n/LanguageContext';
import { uploadCarImage } from '@/lib/uploadCarImage';

const AddCarPage = () => {
  const { settings } = useSettings();
  const { t } = useI18n();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { fetchCarsFromSupabase, updateCar: updateCarInContext } = useCar();
  
  const [step, setStep] = useState<1 | 2>(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [make, setMake] = useState('');
  const [model, setModel] = useState('');
  const [nickname, setNickname] = useState('');
  const [year, setYear] = useState<number | ''>('');
  const [vin, setVin] = useState('');
  const [transmission, setTransmission] = useState<'manual' | 'automatic' | ''>('');
  const [fuelType, setFuelType] = useState<'gasoline' | 'diesel' | 'electric' | 'hybrid' | 'lpg' | 'cng' | ''>('');
  const [mileage, setMileage] = useState<number | ''>('');
  const [licensePlate, setLicensePlate] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [localPreview, setLocalPreview] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const validateStep1 = () => {
    if (!make || !model || !year || !fuelType || !transmission || !licensePlate?.trim()) {
      toast({
        title: t('cars.addCarPage.incompleteTitle'),
        description: t('cars.addCarPage.incompleteDescription'),
        variant: "destructive",
      });
      return false;
    }
    return true;
  };

  const handleContinue = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!validateStep1()) return;
    setStep(2);
  };

  const handleCancel = () => navigate('/cars');
  const handleBack = () => setStep(1);

  const handleAddImageClick = () => fileInputRef.current?.click();
  const handleImageInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files?.length) return;
    const file = e.target.files[0];
    if (localPreview) URL.revokeObjectURL(localPreview);
    setLocalPreview(URL.createObjectURL(file));
    setSelectedFile(file);
  };

  const saveCar = async (uploadPhoto: boolean) => {
    if (isSubmitting || !user) return;
    setIsSubmitting(true);
    try {
      const { data: car, error: carError } = await supabase
        .from('cars')
        .insert({
          make,
          model,
          year: Number(year),
          vin: vin?.trim() || null,
          transmission,
          fuel_type: fuelType || null,
          nickname,
          status: 'active',
          created_by: user.id,
          license_plate: licensePlate?.trim() || null,
        })
        .select()
        .single();

      if (carError) throw carError;
      if (!car) throw new Error('CARS_ADD_CREATE_FAILED');

      const { error: linkError } = await supabase
        .from('user_cars')
        .insert({ user_id: user.id, car_id: car.id });
      if (linkError) throw linkError;

      if (uploadPhoto && selectedFile) {
        const { data: { session } } = await supabase.auth.getSession();
        if (session) {
          try {
            const imagePath = await uploadCarImage({
              file: selectedFile,
              userId: user.id,
              entityId: car.id,
              type: 'car',
              accessToken: session.access_token,
            });
            await updateCarInContext(car.id, { image_path: imagePath });
          } catch (uploadErr) {
            console.warn('Image upload failed:', uploadErr);
          }
        }
      }

      if (mileage !== '' && mileage != null) {
        const reading = Number(mileage);
        if (!Number.isNaN(reading)) {
          const readingKm = settings?.distanceUnit === 'mi' ? Math.round(reading * 1.609344) : reading;
          await supabase.from('odometer_readings').insert({
            car_id: car.id,
            entered_by: user.id,
            reading: readingKm,
            source: 'manual',
          });
        }
      }

      await fetchCarsFromSupabase();
      toast({
        title: t('cars.addCarPage.successTitle'),
        description: t('cars.addCarPage.successDescription', { make, model }),
      });
      setMake('');
      setModel('');
      setNickname('');
      setYear('');
      setVin('');
      setTransmission('');
      setFuelType('');
      setMileage('');
      setLicensePlate('');
      setSelectedFile(null);
      if (localPreview) {
        URL.revokeObjectURL(localPreview);
        setLocalPreview(null);
      }
      setTimeout(() => navigate('/cars'), 500);
    } catch (error) {
      console.error('Failed to add car:', error);
      let errorMessage = t('cars.addCarPage.errorGeneric');
      if (error instanceof PostgrestError) {
        switch (error.code) {
          case '23505':
            errorMessage = t('cars.addCarPage.errorVinExists');
            break;
          case '23503':
            errorMessage = t('cars.addCarPage.errorAuth');
            break;
          default:
            errorMessage = error.message;
        }
      } else if (error instanceof Error) {
        errorMessage = error.message === 'CARS_ADD_CREATE_FAILED' ? t('cars.addCarPage.errorCreateFailed') : error.message;
      }
      toast({ title: t('common.error'), description: errorMessage, variant: "destructive" });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSkip = () => saveCar(false);
  const handleSaveCar = () => saveCar(true);

  return (
    <PageLayout 
      title={t('cars.addCar')} 
      showBackButton 
      onBack={() => (step === 1 ? navigate('/cars') : setStep(1))}
    >
      <div className="max-w-md mx-auto px-4 pb-24">
        {step === 1 ? (
        <form onSubmit={handleContinue} className="space-y-4">
          <div className="rounded-2xl bg-white shadow-md p-4 space-y-4">
            <div>
              <label htmlFor="make" className="block text-sm text-gray-600 mb-2">
                {t('car.make')} *
              </label>
              <Input
                id="make"
                type="text"
                value={make}
                onChange={(e) => setMake(e.target.value)}
                required
                className="w-full h-10 rounded-xl border border-gray-300 bg-white px-3 text-sm placeholder:text-gray-400"
                placeholder={t('cars.edit.brandPlaceholder')}
              />
            </div>

            <div>
              <label htmlFor="model" className="block text-sm text-gray-600 mb-2">
                {t('car.model')} *
              </label>
              <Input
                id="model"
                type="text"
                value={model}
                onChange={(e) => setModel(e.target.value)}
                required
                className="w-full h-10 rounded-xl border border-gray-300 bg-white px-3 text-sm placeholder:text-gray-400"
                placeholder={t('cars.edit.modelPlaceholder')}
              />
            </div>

            <div>
              <label htmlFor="year" className="block text-sm text-gray-600 mb-2">
                {t('car.year')} *
              </label>
              <Input
                id="year"
                type="number"
                min={1900}
                max={new Date().getFullYear()}
                value={year === '' ? '' : year}
                onChange={(e) => setYear(e.target.value ? Number(e.target.value) : '')}
                required
                className="w-full h-10 rounded-xl border border-gray-300 bg-white px-3 text-sm placeholder:text-gray-400"
                placeholder={t('cars.edit.yearPlaceholder')}
              />
            </div>

            <div>
              <label htmlFor="fuelType" className="block text-sm text-gray-600 mb-2">
                {t('car.fuelTypeLabel') || 'Fuel type'} *
              </label>
              <Select
                value={fuelType}
                onValueChange={(value) => setFuelType(value as typeof fuelType)}
                disabled={isSubmitting}
              >
                <SelectTrigger className="w-full h-10 rounded-xl border border-gray-300 bg-white px-3 text-sm">
                  <SelectValue placeholder={t('cars.edit.fuelTypePlaceholder') || 'Select fuel type'} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="gasoline">{t('car.fuelType.gasoline') || 'Gasoline'}</SelectItem>
                  <SelectItem value="diesel">{t('car.fuelType.diesel') || 'Diesel'}</SelectItem>
                  <SelectItem value="electric">{t('car.fuelType.electric') || 'Electric'}</SelectItem>
                  <SelectItem value="hybrid">{t('car.fuelType.hybrid') || 'Hybrid'}</SelectItem>
                  <SelectItem value="lpg">{t('car.fuelType.lpg') || 'LPG'}</SelectItem>
                  <SelectItem value="cng">{t('car.fuelType.cng') || 'CNG'}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <label htmlFor="transmission" className="block text-sm text-gray-600 mb-2">
                {t('car.transmissionLabel') || 'Transmission'} *
              </label>
              <Select
                value={transmission}
                onValueChange={(value) => setTransmission(value as 'manual' | 'automatic' | '')}
                disabled={isSubmitting}
              >
                <SelectTrigger className="w-full h-10 rounded-xl border border-gray-300 bg-white px-3 text-sm">
                  <SelectValue placeholder={t('cars.edit.transmissionPlaceholder') || 'Select transmission'} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="manual">{t('car.transmission.manual') || t('car.manual') || 'Manual'}</SelectItem>
                  <SelectItem value="automatic">{t('car.transmission.automatic') || t('car.automatic') || 'Automatic'}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <label htmlFor="licensePlate" className="block text-sm text-gray-600 mb-2">
                {t('car.licensePlate')} *
              </label>
              <Input
                id="licensePlate"
                type="text"
                value={licensePlate}
                onChange={(e) => setLicensePlate(e.target.value)}
                className="w-full h-10 rounded-xl border border-gray-300 bg-white px-3 text-sm placeholder:text-gray-400"
                placeholder={t('cars.edit.licensePlatePlaceholder')}
              />
            </div>

            <div>
              <label htmlFor="vin" className="block text-sm text-gray-600 mb-2">
                {t('car.vin')} {t('common.optionalField')}
              </label>
              <Input
                id="vin"
                type="text"
                value={vin}
                onChange={(e) => setVin(e.target.value)}
                className="w-full h-10 rounded-xl border border-gray-300 bg-white px-3 text-sm placeholder:text-gray-400"
                placeholder={t('cars.edit.vinPlaceholder')}
              />
            </div>

            <div>
              <label htmlFor="mileage" className="block text-sm text-gray-600 mb-2">
                {t('car.mileage')} {t('common.optionalField')}
              </label>
              <Input
                id="mileage"
                type="number"
                min={0}
                value={mileage === '' ? '' : mileage}
                onChange={(e) => setMileage(e.target.value ? Number(e.target.value) : '')}
                className="w-full h-10 rounded-xl border border-gray-300 bg-white px-3 text-sm placeholder:text-gray-400"
                placeholder={t('cars.edit.mileagePlaceholder')}
              />
            </div>

            <div>
              <label htmlFor="nickname" className="block text-sm text-gray-600 mb-2">
                {t('car.nickname')} {t('common.optionalField')}
              </label>
              <Input
                id="nickname"
                type="text"
                value={nickname}
                onChange={(e) => setNickname(e.target.value)}
                className="w-full h-10 rounded-xl border border-gray-300 bg-white px-3 text-sm placeholder:text-gray-400"
                placeholder={t('cars.edit.nicknamePlaceholder') || "Mom's car"}
              />
            </div>
          </div>

          <div className="flex gap-3">
            <Button
              type="button"
              variant="secondary"
              className="flex-1 rounded-xl text-black text-sm font-medium"
              onClick={handleCancel}
            >
              {t('common.cancel') || 'Cancel'}
            </Button>
            <Button
              type="submit"
              className="flex-1 rounded-xl bg-[#e7fe52] text-black text-sm font-medium hover:bg-[#e7fe52]/90"
            >
              {t('common.continue') || 'Continue'}
            </Button>
          </div>
        </form>
        ) : (
        <>
          <div className="rounded-2xl bg-white shadow-md p-4 mb-4">
            <p className="text-sm text-gray-600 mb-3">
              {t('cars.uploadPhotoLabel') || 'Upload a photo of your car'}
            </p>
            <div
              className="relative flex items-center justify-center w-full rounded-xl overflow-hidden border-2 border-gray-300 bg-gray-50"
              style={{ aspectRatio: '2 / 1' }}
            >
              {(localPreview || selectedFile) ? (
                <img
                  src={localPreview!}
                  alt=""
                  className="object-cover w-full h-full"
                />
              ) : (
                <button
                  type="button"
                  onClick={handleAddImageClick}
                  className="w-full h-full flex flex-col items-center justify-center hover:border-lime transition-colors"
                  disabled={isSubmitting}
                >
                  <img
                    src="/icons/AddCarImage.png"
                    alt=""
                    className="w-12 h-12 opacity-60"
                    style={{ filter: 'invert(0%) sepia(0%) saturate(0%) hue-rotate(0deg) brightness(0) contrast(100%)' }}
                  />
                </button>
              )}
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleImageInputChange}
              className="hidden"
            />
          </div>

          <button
            type="button"
            onClick={handleSkip}
            disabled={isSubmitting}
            className="text-sm text-gray-600 underline hover:text-gray-800 disabled:opacity-50 w-full text-center mb-4"
          >
            {t('carSharing.skip') || 'Skip'}
          </button>

          <div className="flex gap-3">
            <Button
              type="button"
              variant="secondary"
              className="flex-1 rounded-xl text-black text-sm font-medium"
              onClick={handleBack}
            >
              {t('common.back') || 'Back'}
            </Button>
            <Button
              type="button"
              className="flex-1 rounded-xl bg-[#e7fe52] text-black text-sm font-medium hover:bg-[#e7fe52]/90 disabled:opacity-50"
              disabled={isSubmitting}
              onClick={handleSaveCar}
            >
              {isSubmitting ? (t('common.saving') || 'Saving...') : (t('car.save') || 'Save car')}
            </Button>
          </div>
        </>
        )}
      </div>
    </PageLayout>
  );
};

export default AddCarPage;
